import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Sparkles,
  Trash2,
  ShoppingBag,
  Calendar,
  Tag,
  X,
  CheckCircle2,
  PackageSearch,
  Wand2,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { toast } from "sonner";
import { useClonedProducts, getSelectedProductId, getClonedProducts, setClonedProducts } from "../../../lib/extension-bridge";
import { api, getErrorMessage } from "../../../lib/api";

const SOURCE_STYLES = {
  Amazon: "bg-amber-100 text-amber-700",
  Flipkart: "bg-blue-100 text-blue-700",
  Meesho: "bg-pink-100 text-pink-700",
  Unknown: "bg-slate-100 text-slate-600",
};

export const ProductsPage = () => {
  const { products, remove, selectForAutofill } = useClonedProducts();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [viewing, setViewing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedId, setSelectedId] = useState(() => getSelectedProductId());
  const [enhancingId, setEnhancingId] = useState(null);

  const sources = useMemo(
    () => ["All", ...Array.from(new Set(products.map((p) => p.sourceMarketplace || "Unknown")))],
    [products],
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return products.filter(
      (p) =>
        (!q || p.title?.toLowerCase().includes(q)) &&
        (filter === "All" || (p.sourceMarketplace || "Unknown") === filter),
    );
  }, [products, query, filter]);

  const markForAutofill = (product) => {
    selectForAutofill(product.id);
    setSelectedId(product.id);
    toast.success("Set for Auto Fill", {
      description: `Open the EasyLister extension on a seller form and hit Auto Fill — “${product.title}” will be used.`,
    });
  };

  const enhanceProduct = async (product) => {
    setEnhancingId(product.id);
    try {
      const { data } = await api.post("/listing/enhance-product", {
        title: product.title,
        description: product.description,
        marketplace: product.sourceMarketplace,
        category: product.category,
        highlights: product.highlights || [],
        charged: true,
      });
      const { enhanced_title, enhanced_description } = data.data;
      const updated = getClonedProducts().map((p) =>
        p.id === product.id
          ? { ...p, title: enhanced_title || p.title, description: enhanced_description || p.description }
          : p,
      );
      setClonedProducts(updated);
      if (viewing?.id === product.id) {
        setViewing((v) => ({ ...v, title: enhanced_title || v.title, description: enhanced_description || v.description }));
      }
      toast.success("Product AI-enhanced!", { description: "Title and description updated." });
    } catch (err) {
      const msg = getErrorMessage(err);
      if (err?.response?.status === 402) {
        toast.error("Subscription required", { description: "Your daily AI enhance limit has been reached. Upgrade your plan." });
      } else {
        toast.error("Enhancement failed", { description: msg });
      }
    } finally {
      setEnhancingId(null);
    }
  };

  const confirmDelete = () => {
    const product = products.find((p) => p.id === deletingId);
    remove(deletingId);
    if (selectedId === deletingId) setSelectedId(null);
    toast.success("Product removed", { description: product?.title });
    setDeletingId(null);
  };

  return (
    <div className="space-y-6" data-testid="panel-products">
      <div className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50/50 to-white p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-purple-600 text-white flex items-center justify-center shadow-lg shadow-purple-600/25 shrink-0">
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-lg font-extrabold text-slate-900">Products</div>
            <div className="text-sm text-slate-600 mt-1">
              Products cloned with the EasyLister extension, synced here automatically.
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => toast.info("Open the EasyLister extension on a product page and click Clone Product.")}
          data-testid="products-clone-button"
          className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 font-semibold"
        >
          <Sparkles className="w-4 h-4 mr-1.5" /> How to clone
        </Button>
      </div>

      {products.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 h-11">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              data-testid="products-search"
              placeholder="Search products by title"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400"
            />
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-white border border-slate-200 p-1 overflow-x-auto">
            {sources.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`products-filter-${f.toLowerCase()}`}
                className={`px-3 h-8 text-xs font-semibold rounded-full transition-colors whitespace-nowrap ${
                  filter === f ? "bg-purple-600 text-white shadow-sm" : "text-slate-600 hover:text-purple-700"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      )}

      {products.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-purple-200 bg-white p-12 text-center" data-testid="products-empty">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-purple-50 text-purple-700 flex items-center justify-center mb-4">
            <PackageSearch className="w-6 h-6" />
          </div>
          <div className="font-heading text-base font-bold text-slate-900">No products cloned yet</div>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
            Install the EasyLister Chrome extension, browse a product on Amazon, Flipkart, or Meesho, and click{" "}
            <strong>Clone Product</strong>. It'll show up here instantly.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => {
            const isSelected = selectedId === p.id;
            return (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`product-card-${p.id}`}
                className={`rounded-2xl border bg-white overflow-hidden hover:shadow-xl hover:shadow-purple-600/5 hover:-translate-y-1 transition-all ${
                  isSelected ? "border-purple-400 ring-2 ring-purple-100" : "border-slate-200"
                }`}
              >
                <button
                  onClick={() => setViewing(p)}
                  className="block w-full aspect-[5/3] bg-gradient-to-br from-purple-100 to-fuchsia-100 overflow-hidden"
                >
                  {p.images?.[0] ? (
                    <img
                      src={p.images[0]}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-purple-300">
                      <ShoppingBag className="w-8 h-8" />
                    </div>
                  )}
                </button>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Badge className={`rounded-full text-[10px] font-bold hover:opacity-90 ${SOURCE_STYLES[p.sourceMarketplace] || SOURCE_STYLES.Unknown}`}>
                      {p.sourceMarketplace || "Unknown"}
                    </Badge>
                    {isSelected && (
                      <Badge className="rounded-full text-[10px] font-bold bg-purple-600 text-white hover:opacity-90">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Set for Auto Fill
                      </Badge>
                    )}
                  </div>
                  <button
                    onClick={() => setViewing(p)}
                    className="font-heading font-bold text-slate-900 text-sm leading-tight line-clamp-2 text-left hover:text-purple-700"
                  >
                    {p.title || "Untitled product"}
                  </button>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <div className="font-bold text-purple-700 tabular-nums">{p.price || "—"}</div>
                    {p.cloneDate && (
                      <div className="text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(p.cloneDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        onClick={() => markForAutofill(p)}
                        data-testid={`product-autofill-${p.id}`}
                        className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold h-8 text-xs"
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Use for Auto Fill
                      </Button>
                      <button
                        onClick={() => setDeletingId(p.id)}
                        data-testid={`product-delete-${p.id}`}
                        aria-label="Delete product"
                        className="p-2 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => enhanceProduct(p)}
                      disabled={enhancingId === p.id}
                      data-testid={`product-enhance-${p.id}`}
                      className="w-full rounded-full border-violet-200 text-violet-700 hover:bg-violet-50 font-semibold h-8 text-xs"
                    >
                      <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                      {enhancingId === p.id ? "Enhancing…" : "AI Enhance"}
                    </Button>
                  </div>
                </div>
              </motion.article>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500 text-sm">
              No products match your search.
            </div>
          )}
        </div>
      )}

      {/* Product detail viewer */}
      <AnimatePresence>
        {viewing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setViewing(null)}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="product-detail-modal"
              className="bg-white rounded-3xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
            >
              <div className="relative">
                <div className="aspect-[5/3] bg-gradient-to-br from-purple-100 to-fuchsia-100 overflow-hidden rounded-t-3xl">
                  {viewing.images?.[0] && (
                    <img src={viewing.images[0]} alt={viewing.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <button
                  onClick={() => setViewing(null)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-slate-600 hover:text-slate-900 shadow"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-1.5">
                  <Badge className={`rounded-full text-[10px] font-bold hover:opacity-90 ${SOURCE_STYLES[viewing.sourceMarketplace] || SOURCE_STYLES.Unknown}`}>
                    {viewing.sourceMarketplace || "Unknown"}
                  </Badge>
                  {viewing.brand && <Badge className="rounded-full text-[10px] font-bold bg-slate-100 text-slate-700">{viewing.brand}</Badge>}
                </div>
                <div className="font-heading text-lg font-extrabold text-slate-900">{viewing.title}</div>
                <div className="font-heading text-xl font-extrabold text-purple-700">{viewing.price}</div>
                {viewing.description && (
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {viewing.description}
                  </p>
                )}
                {(viewing.variants?.length > 0 || viewing.colors?.length > 0) && (
                  <div className="grid grid-cols-2 gap-3">
                    {viewing.variants?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1">
                          <Tag className="w-3 h-3" /> Sizes
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {viewing.variants.map((v) => (
                            <span key={v} className="px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-semibold text-slate-700">
                              {v}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {viewing.colors?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1">
                          <Tag className="w-3 h-3" /> Colors
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {viewing.colors.map((c) => (
                            <span key={c} className="px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-semibold text-slate-700">
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {viewing.specifications && Object.keys(viewing.specifications).length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Specifications</div>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(viewing.specifications).map(([k, v]) => (
                        <div key={k} className="bg-slate-50 rounded-lg p-2">
                          <div className="text-[10px] text-slate-400 truncate">{k}</div>
                          <div className="text-xs font-semibold text-slate-700 line-clamp-1">{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Button
                    onClick={() => {
                      markForAutofill(viewing);
                      setViewing(null);
                    }}
                    className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold h-10"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" /> Use for Auto Fill
                  </Button>
                  <Button
                    variant="outline"
                    disabled={enhancingId === viewing.id}
                    onClick={() => enhanceProduct(viewing)}
                    className="w-full rounded-full border-violet-200 text-violet-700 hover:bg-violet-50 font-semibold h-10"
                  >
                    <Wand2 className="w-4 h-4 mr-1.5" />
                    {enhancingId === viewing.id ? "Enhancing…" : "AI Enhance"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation */}
      <AlertDialog open={Boolean(deletingId)} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent data-testid="product-delete-confirm" className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove this product?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes it from your cloned product library in the extension too.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="product-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="product-delete-confirm-button"
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
